import { DEFAULT_LOCALE, getLocaleDirection, type Locale } from '@syncanix/shared';
import {
  createContext,
  startTransition,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { catalogStore } from '@/i18n/catalog-store';
import { createTranslator } from '@/i18n/translator';

import type { Translator } from '@/i18n/translator';
import type { PropsWithChildren, ReactElement } from 'react';

/**
 * Marketing-site i18n context (`{ locale, setLocale, t }`).
 *
 * **The URL is the single source of truth for the locale** (GA-13 / ADR-0133):
 * every route is published per-locale (English unprefixed; others under
 * `/<locale>/…`), so the document's locale is whatever its URL encodes. The
 * provider is seeded from the URL — the SSR prerender renders each document in
 * its URL's locale and the client entry hydrates the same locale — and
 * {@link LocaleSync} keeps `locale` pinned to the URL across client navigations.
 * There is intentionally NO `localStorage`/`navigator` auto-switch that would
 * diverge the rendered locale from the URL (and from the canonical/`hreflang`).
 *
 * EVERY catalog piece (per-locale × per-group, ADR-0195) is a dynamically
 * imported chunk owned by the app-wide {@link catalogStore}; the client entry
 * awaits the active document's pieces before hydrating and passes the resolved
 * translator as `initialTranslator`. This provider then only *follows* the
 * store — `LocaleSync` and the lazy-route factories drive it as the URL
 * changes. The key-echo default below only renders on unseeded (bare-test)
 * mounts until the store's core catalog resolves. Each locale change rewrites
 * `<html lang>` + `<html dir>` so RTL (ar/he) flips the whole page.
 */

/** Renders raw keys; visible only on unseeded mounts until the catalog resolves. */
const keyEchoTranslator: Translator = createTranslator({});

export interface I18nContextValue {
  readonly locale: Locale;
  readonly setLocale: (locale: Locale) => void;
  readonly t: Translator;
}

const I18nContext = createContext<I18nContextValue | null>(null);

export interface I18nProviderProps {
  /**
   * The starting locale, derived from the URL. Set by the SSR entry (each route
   * is prerendered under the locale its URL encodes) and by the client entry
   * (which derives it from the live URL before hydrating). Defaults to English.
   */
  readonly initialLocale?: Locale;
  /**
   * The synchronous translator to seed first render with, paired with
   * {@link initialLocale}. The prerender passes a fully-resolved server
   * translator; the client passes the awaited active-locale translator before
   * hydrating, so the first client render matches the in-language server markup
   * (no hydration mismatch). Omitted → the EN baseline, swapped in by the effect.
   */
  readonly initialTranslator?: Translator;
}

export function I18nProvider({
  children,
  initialLocale,
  initialTranslator,
}: PropsWithChildren<I18nProviderProps>): ReactElement {
  const [locale, setLocaleState] = useState<Locale>(() => initialLocale ?? DEFAULT_LOCALE);
  // Seed with the provided in-language translator when given (SSR / hydration);
  // otherwise start on the EN baseline and let the effect below swap in the
  // active-locale translator once its catalog chunk resolves.
  const [translator, setTranslator] = useState<Translator>(
    () => initialTranslator ?? keyEchoTranslator,
  );

  // Follow the app-wide catalog store (ADR-0195): a locale switch resolved by
  // LocaleSync or a route factory's group arriving swaps the translator in.
  // startTransition: the async catalog swap can land mid-hydration; a sync
  // setState while React is still hydrating the route <Suspense> boundary
  // would force it to discard the server HTML and client-render.
  useEffect(() => {
    return catalogStore.subscribe(() => {
      startTransition(() => {
        setTranslator(() => catalogStore.getTranslator());
      });
    });
  }, []);

  // The locale whose catalog this provider already holds. A SEEDED mount
  // starts resolved for its initial locale — the entry (or a test) supplied
  // that locale's translator, and re-loading here would swap a fully-seeded
  // test translator for a narrower store one. An UNSEEDED mount starts
  // unresolved so the first effect run loads at least the core catalog.
  const resolvedLocaleRef = useRef<Locale | null>(
    initialTranslator !== undefined ? (initialLocale ?? DEFAULT_LOCALE) : null,
  );

  useEffect(() => {
    if (typeof document !== 'undefined') {
      document.documentElement.lang = locale;
      document.documentElement.dir = getLocaleDirection(locale);
    }
    // `setLocale` contract: switching locale ALWAYS resolves that locale's
    // catalog (the store re-fetches every accumulated group in the new
    // locale and notifies the subscription above).
    if (resolvedLocaleRef.current !== locale) {
      resolvedLocaleRef.current = locale;
      void catalogStore.ensureCatalog(locale, ['core']);
    }
  }, [locale]);

  const setLocale = useCallback((next: Locale) => {
    setLocaleState(next);
  }, []);

  const value = useMemo<I18nContextValue>(
    () => ({ locale, setLocale, t: translator }),
    [locale, setLocale, translator],
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components -- co-located hook by design
export function useT(): I18nContextValue {
  const ctx = useContext(I18nContext);
  if (ctx === null) {
    throw new Error('useT() must be used inside <I18nProvider> (apps/marketing/src/core).');
  }
  return ctx;
}

/**
 * The active locale, or {@link DEFAULT_LOCALE} when there is no provider. Unlike
 * {@link useT} this never throws — for navigational primitives ({@link
 * LocalizedLink}) that must render correctly even when mounted outside the
 * provider (e.g. an isolated component test), defaulting to the English
 * (unprefixed) path rather than crashing.
 */
// eslint-disable-next-line react-refresh/only-export-components -- co-located hook by design
export function useOptionalLocale(): Locale {
  return useContext(I18nContext)?.locale ?? DEFAULT_LOCALE;
}
